import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { blogApi } from "../services/api";
import { RichContent } from "../components/RichContent";
import ShareButtons from "../components/ShareButtons";

function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blogApi.getBySlug(slug);
      setPost(response.data);
    } catch (err) {
      console.error("Error loading blog post:", err);
      setError("Blog post not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-red-800 mb-4">
            Blog Post Not Found
          </h1>
          <p className="text-red-600 mb-6">
            {error || "The blog post you are looking for does not exist."}
          </p>
          <Link
            to="/insights"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{post.title} | Innobig</title>
        <meta name="description" content={post.excerpt || post.title} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:url" content={window.location.href} />
        {post.featured_image && (
          <meta property="og:image" content={post.featured_image} />
        )}
        {post.featured_image && (
          <meta property="og:image:alt" content={post.title} />
        )}
        <meta property="og:site_name" content="Innobig" />
        {post.published_at && (
          <meta property="article:published_time" content={post.published_at} />
        )}
        {post.author && (
          <meta property="article:author" content={post.author} />
        )}
        {post.category && (
          <meta property="article:section" content={post.category} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        {post.featured_image && (
          <meta name="twitter:image" content={post.featured_image} />
        )}

        {/* Additional SEO */}
        {post.category && <meta name="keywords" content={post.category} />}
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F3A5F] to-[#3B82F6] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link
              to="/insights"
              className="text-blue-100 hover:text-white transition"
            >
              ← Back to Insights
            </Link>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{post.title}</h1>
          <p className="text-blue-100 text-lg">{post.excerpt}</p>
          <div className="flex items-center gap-6 mt-6 text-blue-100 text-sm">
            <span>By {post.author || "Innobig"}</span>
            {post.published_at && (
              <span>
                {new Date(post.published_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            )}
            {post.category && (
              <span className="inline-block bg-blue-500 bg-opacity-30 px-3 py-1 rounded-full">
                {post.category}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="bg-white py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Share Buttons */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <ShareButtons title={post.title} description={post.excerpt} />
          </div>

          <div className="prose prose-lg max-w-none">
            {post.content ? (
              <RichContent html={post.content} />
            ) : (
              <p>{post.excerpt}</p>
            )}
          </div>

          {/* Share Buttons at Bottom */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <ShareButtons title={post.title} description={post.excerpt} />
          </div>
        </div>
      </section>

      {/* Related Posts CTA */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">More Insights</h2>
          <p className="text-gray-600 mb-8">
            Explore more articles and insights to grow your business.
          </p>
          <Link
            to="/insights"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            View All Articles
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogPostPage;
